<template>
    <div class="filter-steps--container">
        <customer-autocomplete
            id="customer-filter"
            :team-region-id="teamRegionId"
            :value="selectedCustomer"
            @input="handleCustomerChanged"
            label="Customer"
            ref="customerAutocomplete"
        />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { handleRequests, showErrorMessage } from '@/helpers';
import CustomerAutocomplete from '@/components/Autocomplete/CustomerAutocomplete';

export default {
    name: 'CustomerMapFilterOptions',
    props: {
        teamRegionId: { type: Number, default: null },
        customerId: { type: Number, default: null }
    },
    components: {
        CustomerAutocomplete
    },
    data() {
        return {
            selectedCustomer: {
                customerId: this.customerId,
                contact: {
                    name: null,
                    email: null,
                    phone: null
                }
            }
        };
    },
    computed: {
        ...mapGetters({
            teamRegions: 'team/teamRegions',
            hasCustomerFilterAccess: 'user/hasInventoryListAccess',
            isSales: 'user/isSales'
        })
    },
    watch: {
        customerId(newVal) {
            if (newVal !== this.selectedCustomer.customerId) {
                this.selectedCustomer = {
                    customerId: newVal,
                    contact: {
                        name: null,
                        email: null,
                        phone: null
                    }
                };
            }
        },
        teamRegionId(newVal) {
            if (newVal) {
                this.getCustomersForMap();
            }
        }
    },
    mounted() {
        // Override the getCustomerList method in the CustomerAutocomplete component
        if (this.$refs.customerAutocomplete) {
            this.$refs.customerAutocomplete.getCustomerList = this.getCustomersForMap;
        }
        // Load customers if teamRegionId is already set
        if (this.teamRegionId) {
            this.getCustomersForMap();
        }
    },
    updated() {
        // Ensure the override is applied after component updates
        if (this.$refs.customerAutocomplete) {
            this.$refs.customerAutocomplete.getCustomerList = this.getCustomersForMap;
        }
    },
    methods: {
        handleCustomerChanged(customer) {
            this.selectedCustomer = customer;
            this.$emit('onFilterChanged', {
                teamRegionId: this.teamRegionId,
                customerId: customer?.customerId || null
            });
        },
        async getCustomersForMap() {
            if (!this.hasCustomerFilterAccess && !this.isSales) {
                return;
            }

            const autocompleteComponent = this.$refs.customerAutocomplete;
            if (!autocompleteComponent) {
                return;
            }

            autocompleteComponent.isAutoCompleteLoading = true;

            try {
                // Use the map-specific endpoint
                const endpoint = `/api/customers/map`;
                const { data } = await handleRequests(endpoint, {
                    params: {
                        teamRegionId: this.teamRegionId,
                        searchText: autocompleteComponent.autocompleteText
                    }
                });

                if (
                    autocompleteComponent.autocompleteText !== null &&
                    autocompleteComponent.autocompleteText !== '' &&
                    autocompleteComponent.version === 'v1'
                ) {
                    autocompleteComponent.isOpen = true;
                }

                autocompleteComponent.autocompleteList = data.customers;
                autocompleteComponent.isAutoCompleteLoading = false;
            } catch (error) {
                const message = 'Error in getting the customer list for map';
                showErrorMessage(this, message, error);
                autocompleteComponent.isAutoCompleteLoading = false;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.filter-steps--container {
    ::v-deep .md-field {
        display: inline-block;
        width: 200px;
        min-height: 48px;
        margin-right: 20px;
        margin-top: 0;
        vertical-align: top;
    }
    .datepicker-container {
        width: 140px;
    }
    .filter-step-date {
        margin-right: 0;
        min-height: initial;
        position: absolute;
        z-index: 1;
        min-width: 250px;
        ::v-deep .md-clear {
            display: none;
        }
        ::v-deep .md-input {
            width: 100%;
        }
    }
    ::v-deep .md-datepicker:after,
    ::v-deep .md-datepicker:before {
        bottom: 0;
        height: 0;
    }
    ::v-deep .md-datepicker:before,
    ::v-deep .md-datepicker:after {
        bottom: 30px;
        content: 'Trip date';
        color: #aaaaaa;
        font-weight: 400;
        font-size: 14px;
        transform: none;
        z-index: 1;
    }
    ::v-deep .md-has-value:before,
    ::v-deep .md-has-value:after {
        bottom: 0;
        height: 0;
        content: '';
    }
    ::v-deep .md-datepicker .md-input {
        position: absolute;
        z-index: 9;
        width: 150px;
    }
    ::v-deep .md-datepicker .md-input-action {
        z-index: 99;
        right: 52px;
    }
}
::v-deep .vs__search::placeholder {
    color: #aaaaaa;
    font-size: 14px;
}
::v-deep .vs--searchable .vs__dropdown-toggle {
    border: none;
    padding-bottom: 5px;
    margin-top: 4px;
    background-color: transparent;
    cursor: pointer;
}
::v-deep .vs--disabled .vs__clear,
::v-deep .vs--disabled .vs__dropdown-toggle,
::v-deep .vs--disabled .vs__open-indicator,
::v-deep .vs--disabled .vs__search,
::v-deep .vs--disabled .vs__selected {
    background-color: transparent;
}
::v-deep .vs__search {
    cursor: pointer;
}
.datepicker-noevent {
    ::v-deep .md-datepicker {
        input {
            pointer-events: none;
        }
    }
}
</style>
